/* =============================================================================
   VIEWS / ADMIN / DASHBOARD-WIDGETS — Widget-/Heatmap-Renderer
   =============================================================================
   S202 (REF-8) — aus dashboard.js extrahiert. Reine HTML-Builder + die
   Count-Up-Animation:
     - sparklineSvg (Inline-SVG-Sparkline)
     - renderWidget / renderDualWidget / renderTripleWidget
     - renderOrderHeatmap (Mini-Heatmap nächste 4 h)
     - triggerWidgetAnimations (Count-Up + Flash beim (Re-)Mount)

   Reine Extraktion, NULL Verhaltensänderung. Inline-Handler bleiben `func()`
   (dom-bindings-Parser, kein `window.func()`) — `jumpToOrdersTab()` in der
   Heatmap wird zur Klickzeit über window aufgelöst (der Orchestrator bindet ihn).
============================================================================ */

// S238-HF2 (Jonas-Entscheid): KEIN Euro-Wert mehr auf dem Dashboard — weder in
// einer Kachel (S237 T2) noch in der Heatmap-Fußzeile oder ihren Spalten-
// Tooltips. Damit hat dieses Modul keinen Währungs-Formatter mehr nötig
// (`centsToEUR` bleibt in dashboard-data.js für andere Konsumenten).
// `getOrderStats().revenueGross` wird weiter berechnet (Teil der Stats-API,
// von der Auswertung genutzt), aber hier nicht mehr angezeigt.

/** Erzeugt eine Inline-SVG-Sparkline aus einer Zahlenreihe. */
export function sparklineSvg(values, opts = {}) {
  if (!values || values.length === 0) return '';
  const w = 100;        // viewBox-Width
  const h = 36;         // viewBox-Height
  const pad = 2;
  const max = Math.max(1, ...values);
  const min = 0;
  const step = (w - pad * 2) / Math.max(1, values.length - 1);
  const points = values.map((v, i) => {
    const x = pad + i * step;
    const ratio = (v - min) / (max - min || 1);
    const y = h - pad - ratio * (h - pad * 2);
    return { x, y };
  });
  const linePath = points.map((p, i) => (i === 0 ? 'M' : 'L') + p.x.toFixed(1) + ',' + p.y.toFixed(1)).join(' ');
  const areaPath = linePath + ` L${points[points.length - 1].x.toFixed(1)},${h - pad} L${points[0].x.toFixed(1)},${h - pad} Z`;
  const stroke = opts.color || 'var(--green)';
  const fill = opts.fill || 'var(--green-glow)';
  // Letzter Punkt als Dot
  const last = points[points.length - 1];
  return `
    <svg viewBox="0 0 ${w} ${h}" preserveAspectRatio="none" aria-hidden="true">
      <path d="${areaPath}" fill="${fill}" />
      <path d="${linePath}" fill="none" stroke="${stroke}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
      <circle cx="${last.x.toFixed(1)}" cy="${last.y.toFixed(1)}" r="2.2" fill="${stroke}" />
    </svg>
  `;
}

/**
 * Dual-Widget: zwei Werte nebeneinander in einer Kachel, getrennt durch eine
 * dünne Linie. Verwendet für kombinierte Heute-/Offen-Kacheln (P8).
 */
export function renderDualWidget({ label, icon, variant, onclick, left, right, sub }) {
  const clickable = onclick ? 'is-clickable' : '';
  const variantClass = variant ? ('widget-' + variant) : '';
  return `
    <div class="widget widget-dual ${variantClass} ${clickable}" ${onclick ? `onclick="${onclick}" tabindex="0"` : ''}>
      <div class="widget-head">
        <span class="widget-label">${label}</span>
        ${icon ? `<span class="widget-icon">${icon}</span>` : ''}
      </div>
      <div class="widget-dual-grid">
        <div class="widget-dual-col">
          <div class="widget-dual-value countup">${left.value}</div>
          <div class="widget-dual-label">${left.label}</div>
          ${left.trend ? `<span class="widget-trend ${left.trend.dir}" title="vs. Vortag">${left.trend.label}</span>` : ''}
        </div>
        <div class="widget-dual-divider"></div>
        <div class="widget-dual-col">
          <div class="widget-dual-value countup">${right.value}</div>
          <div class="widget-dual-label">${right.label}</div>
          ${right.trend ? `<span class="widget-trend ${right.trend.dir}" title="vs. Vortag">${right.trend.label}</span>` : ''}
        </div>
      </div>
      ${sub ? `<div class="widget-sub" style="margin-top: var(--space-3);">${sub}</div>` : ''}
    </div>
  `;
}

/**
 * S122 — Triple-Widget: drei Werte nebeneinander (Reserv. · Bestell. · Lieferung),
 * getrennt durch zwei dünne Linien. Analog `renderDualWidget`, aber dreispaltig.
 * Genutzt für die KPI-Kacheln „Heute" + „Offen", seit Lieferungen als
 * vollwertige dritte Spalte mitgezählt werden. Test-Hook (S122).
 */
export function renderTripleWidget({ label, icon, variant, onclick, left, mid, right, sub }) {
  const clickable = onclick ? 'is-clickable' : '';
  const variantClass = variant ? ('widget-' + variant) : '';
  const col = (c) => `
        <div class="widget-dual-col">
          <div class="widget-dual-value countup">${c.value}</div>
          <div class="widget-dual-label">${c.label}</div>
          ${c.trend ? `<span class="widget-trend ${c.trend.dir}" title="vs. Vortag">${c.trend.label}</span>` : ''}
        </div>`;
  return `
    <div class="widget widget-triple ${variantClass} ${clickable}" ${onclick ? `onclick="${onclick}" tabindex="0"` : ''}>
      <div class="widget-head">
        <span class="widget-label">${label}</span>
        ${icon ? `<span class="widget-icon">${icon}</span>` : ''}
      </div>
      <div class="widget-dual-grid">
        ${col(left)}
        <div class="widget-dual-divider"></div>
        ${col(mid)}
        <div class="widget-dual-divider"></div>
        ${col(right)}
      </div>
      ${sub ? `<div class="widget-sub" style="margin-top: var(--space-3);">${sub}</div>` : ''}
    </div>
  `;
}

/** Modernes Widget. */
export function renderWidget({ label, value, sub, icon, trend, sparkline, sparkColor, sparkFill, onclick, variant }) {
  const clickable = onclick ? 'is-clickable' : '';
  const variantClass = variant ? ('widget-' + variant) : '';
  const trendHtml = trend
    ? `<span class="widget-trend ${trend.dir}" title="vs. Vortag">${trend.label}</span>`
    : '';
  const sparkHtml = sparkline && sparkline.length
    ? `<div class="widget-sparkline">${sparklineSvg(sparkline, { color: sparkColor, fill: sparkFill })}</div>`
    : '';
  return `
    <div class="widget ${variantClass} ${clickable}" ${onclick ? `onclick="${onclick}" tabindex="0"` : ''}>
      <div class="widget-head">
        <span class="widget-label">${label}</span>
        ${icon ? `<span class="widget-icon">${icon}</span>` : ''}
      </div>
      <div class="widget-value countup">${value}</div>
      ${sub ? `<div class="widget-sub">${sub}</div>` : ''}
      ${trendHtml}
      ${sparkHtml}
    </div>
  `;
}

/**
 * Mini-Heatmap der nächsten 4 Stunden Abhol-Slots (15-Min-Buckets). Klick auf
 * eine Spalte mit ≥1 Bestellung springt zum Bestellungs-Tab. Wird nur
 * gerendert, wenn Bestellungen aktiv sind oder zumindest welche heute liefen.
 */
export function renderOrderHeatmap(slots, maxCount, stats) {
  if (!slots || slots.length === 0) return '';
  // Nichts zeigen, wenn heute noch nichts los war UND auch nichts ansteht.
  const totalUpcoming = slots.reduce((s, x) => s + x.count, 0);
  if (totalUpcoming === 0 && (!stats || stats.count === 0)) return '';

  const bars = slots.map(s => {
    const pct = Math.round((s.count / maxCount) * 100);
    const heightPx = Math.max(6, Math.round(pct * 0.6)); // 0..60 px
    const isUrgent = s.count >= Math.max(2, Math.ceil(maxCount * 0.66));
    const clickable = s.count > 0;
    return `
      <div class="order-heatmap-col ${clickable ? 'clickable' : ''}" ${clickable ? 'onclick="jumpToOrdersTab()"' : ''} title="${s.label} · ${s.count} Bestellung${s.count===1?'':'en'}">
        <div class="order-heatmap-bar-wrap">
          <div class="order-heatmap-bar ${isUrgent ? 'urgent' : ''}" style="height:${heightPx}px;"></div>
        </div>
        <div class="order-heatmap-count">${s.count || ''}</div>
        <div class="order-heatmap-label">${s.label}</div>
      </div>
    `;
  }).join('');

  return `
    <div class="card" style="margin-top: 20px;">
      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom: 8px; flex-wrap: wrap; gap: 8px;">
        <h2 style="margin:0;">Bestellungen — nächste 4 Stunden</h2>
        <span style="font-size:.8rem; color:var(--ink-muted);">15-Min-Slots · Klick auf eine Spalte → Bestellungs-Tab</span>
      </div>
      <p style="font-size:.78rem; color:var(--ink-muted); margin: 0 0 10px;">
        Offen heute: <strong>${stats.openCount}</strong> · gesamt heute: <strong>${stats.count}</strong>.
      </p>
      <div class="order-heatmap" role="list">${bars}</div>
    </div>
  `;
}

/**
 * Sucht nach .countup-Elementen und animiert die Werte hoch (nur Zahlen) bzw.
 * setzt die is-flash-Klasse für 1.2 s. Idempotent — bei jedem Re-Render aufrufbar.
 */
export function triggerWidgetAnimations(root) {
  if (!root) return;
  // Beim allerersten Mount: alle Counter sanft hochzählen
  const counters = root.querySelectorAll('.countup');
  counters.forEach(el => {
    const raw = (el.textContent || '').trim();
    // Nur reine Zahlen animieren (z.B. "23", "1.250,50 €" lassen wir wie es ist)
    if (!/^\d+$/.test(raw)) return;
    const target = parseInt(raw, 10);
    if (!Number.isFinite(target) || target === 0) return;
    if (target < 4) return; // bei sehr kleinen Werten lohnt die Animation nicht
    const dur = 600;
    const start = performance.now();
    function step(now) {
      const t = Math.min(1, (now - start) / dur);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      el.textContent = String(Math.round(target * eased));
      if (t < 1) requestAnimationFrame(step);
      else el.textContent = String(target);
    }
    requestAnimationFrame(step);
  });
}

// Backward-Compat — renderOrderHeatmap ist window-gebunden (Registry/Legacy).
if (typeof window !== 'undefined') {
  Object.assign(window, { renderOrderHeatmap });
}
